/**
 * Website entry point.
 *
 * This file is the entry point for the website and is responsible for importing
 * the necessary assets, initializing the application, and fetching the application
 * docket from the API.
 *
 * @since 2.0.0
 * @version 3.0.0
 * @module index
 */

/**
 * Import JavaScript Assets
 * @since 3.0.0
 */
import { Cookies } from "commonAssets/cookies";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { Header } from "commonAssets/components/Header";
import { Navigation } from "commonAssets/components/Navigation";
import { OptOut } from "commonAssets/components/Forms/OptOut";
import { Footer } from "commonAssets/components/Footer";
import { ImportantDates } from "commonAssets/components/ImportantDates";
import { Page } from "commonAssets/pages/Page";
import { siteTitle } from "commonAssets/lib/siteTitle.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { I18n } from "commonAssets/i18n";
import {
    loading,
    appConfigProd,
    appDocket,
    appClaimForm,
    siteInfoLang,
    userLanguage,
    setSiteInfo,
    setSiteInfoLang,
    setEmailInform,
    setPhoneAdmin,
    setEmailConfirm,
    setUserLanguage,
    setAppLocale,
    setI18n,
} from "commonAssets/js/constants";
import languages from "./languages";
import siteConfig from "./config";
import {
    resolveDocketAPI,
    resolvePageSlug,
    currentDocketSlug,
} from "./scripts/resolveRoute";
import { initPathRouting } from "./scripts/pathRouting";
import { insertRootHubLinks } from "./scripts/rootHub";
import { insertUpdateBox } from "./scripts/updateBox";
import {
    withOtherSettlementsLink,
    withoutSubmitClaim,
} from "./scripts/subsiteNav";
import { markPassedImportantDates } from "./scripts/importantDates";
import { initMediaTags } from "./scripts/mediaTags";

/**
 * Docket slugs for this multi-docket site. Read directly from the local config
 * (the platform's webpack DefinePlugin only injects a fixed set of keys, so a
 * new config key would not otherwise reach the bundle).
 * @type {string[]}
 */
const pathDockets = siteConfig.pathDockets || [];

/**
 * Phase 1: the claim filing period is not open, so the claim form is suppressed
 * — the submit-claim route redirects to the docket home and the submit-claim
 * nav link / "Submit Claim" button are hidden. Flip to `false` (and set
 * `appPayMethod`) to re-enable the claim form when claims open.
 * @type {boolean}
 */
const CLAIM_FORM_SUPPRESSED = true;

/**
 * Import Styles
 * @since 3.2.3
 */
import "./style/custom.css";

/**
 * Import Custom JavaScript Script
 * @since 3.0.0
 */
import {
    customInitClaim,
    customProcessClaim,
    customLoadMember,
} from "./scripts/custom";
import { customSubmitClaim } from "./scripts/SubmitClaim/index.mjs";

// Initialize error handler
const errorHandler = new ErrorHandler();

/**
 * Internationalization (i18n) Handling
 *
 * Sets the languages available for the distribution.
 *
 * Examples:
 * i18n.languages returns languages in an array
 * i18n.userLanguage returns current languages
 * i18n.getTranslation('string') returns predefined string
 */
const i18n = I18n.create(siteConfig.languages || ["en"], {}, languages);
setI18n(() => i18n);

/**
 * Cookies
 * @returns {void}
 */
const set_cookies = new Cookies();
set_cookies.init();

/**
 * Set the user's language using the value from `set_cookies.language()`.
 * @param {Function} setCookiesLanguage - The function that returns the user's language.
 */
setUserLanguage(() => set_cookies.language());

/**
 * Set the application locale using the value from `set_cookies.locale()`.
 * @param {Function} setCookiesLocale - The function that returns the application locale.
 */
setAppLocale(() => set_cookies.locale());

/**
 * Loading GIF
 */
loadingGif(loading, "show");

/**
 * Initialize the analytics and media tags. `getPage` calls this on every route
 * render, so the work is delegated to the SPA-safe `initMediaTags()` (GA, FB
 * Pixel, Trade Desk, RFI) which guards external loaders against re-injection
 * and fires page-view beacons once per route.
 */
export function analytics() {
    initMediaTags();
}

/**
 * Calls the appropriate function to display page content based on the given domainSlug.
 * If domainSlug is empty, the 'home' case will be executed.
 *
 * @param {Array} sitePages - An array containing the website sitePages' information.
 * @param {string} domainSlug - The unique identifier (slug) of the page to be displayed.
 * @param {} importantDates -
 *
 * @example
 * // Assuming sitePages array and domainSlug are defined and valid
 * getPage(sitePages, domainSlug, importantDates)
 */
export async function getPage(sitePages, domainSlug, importantDates) {
    // Show the loading gif
    loadingGif(loading, "show");

    // Set the site title
    if (domainSlug === "") {
        siteTitle(sitePages);
    } else {
        siteTitle(sitePages, domainSlug);
    }

    // Misc handling.
    domainSlug = domainSlug || "home";

    // Create a new page instance
    const page = new Page(sitePages, domainSlug, importantDates);
    switch (domainSlug) {
        case "home":
            page.home();
            // The legal-options table is not used on any home page — neither
            // the root hub nor the settlement subsites — so remove it on every
            // home render.
            document.getElementById("optionsDIV_home")?.remove();
            // The root hub is a thin router; the shared home renderer's
            // Important Dates card belongs to the settlement subsites only, and
            // the root-hub click-through links stand in for it.
            const homeDocket = currentDocketSlug(
                window.location.pathname,
                pathDockets
            );
            if (homeDocket === "") {
                document.getElementById("ImportantDates")?.remove();
                insertRootHubLinks(
                    document.getElementById("official-settlement-website-hero")
                );
            }
            // Hardcoded July 2026 distribution update box, above the banner on
            // the University of Chicago settlement home page only.
            if (homeDocket === "university-of-chicago-settlement") {
                insertUpdateBox(
                    document.getElementById("official-settlement-website-hero")
                );
            }
            analytics();
            break;

        case "demo":
            page.default();
            analytics();
            break;

        case "submit-claim":
            // Phase 1: claim form suppressed. Redirect any direct access back to
            // the docket home instead of rendering the form.
            if (CLAIM_FORM_SUPPRESSED) {
                const claimDocket = currentDocketSlug(
                    window.location.pathname,
                    pathDockets
                );
                window.location.replace(claimDocket ? `/${claimDocket}` : "/");
                break;
            }

            // Use test mode for local development to bypass API deadline checks
            // This allows developers to test HTML partials without API dependencies
            const isLocalDevelopment =
                window.location.hostname === "localhost" ||
                window.location.hostname.startsWith("local.");
            
            let SubmitClaimClass = customSubmitClaim;
            
            // Dynamically import test class only in localhost to avoid bundling in production
            if (isLocalDevelopment) {
                try {
                    const { testSubmitClaim } = await import("./scripts/SubmitClaim/testSubmitClaim.mjs");
                    SubmitClaimClass = testSubmitClaim;
                    console.log("Using test mode for localhost development");
                } catch (error) {
                    console.warn("Failed to load test mode, falling back to production mode:", error);
                    // Fall back to production class if test import fails
                }
            }
            
            const submit_claim = SubmitClaimClass.createInstance({
                appClaimForm,
                userLanguage,
                customInitClaim,
                customProcessClaim,
                customLoadMember,
                errorHandler,
            });
            submit_claim.form();
            analytics();
            break;

        case "opt-out":
            const opt_out = OptOut.createInstance({
                appClaimForm,
                userLanguage,
                i18n,
                errorHandler,
            });
            opt_out.form();
            analytics();
            break;

        case "important-documents":
            page.important_documents();
            analytics();
            break;

        case "faqs":
            page.faqs();
            analytics();
            break;

        case "contact-us":
            page.contact();
            analytics();
            break;

        case "en":
            window.location.href = "/?language=en";
            break;

        case "es":
            window.location.href = "/?language=es";
            break;

        default:
            page.error();
            analytics();
    }

    // Strike through any Important Dates whose deadline has passed and append a
    // bold red [PASSED] (no-op on pages without the dates card).
    markPassedImportantDates(importantDates?.params?.findResult?.ImportantDates);
}

/**
 * Fetches the application docket from the API and updates the site information.
 *
 * This function performs the following steps:
 * 1. Fetches data from the `appDocketAPI`.
 * 2. Checks the response status and handles errors if the status is greater than 299.
 * 3. Parses the response JSON data.
 * 4. Updates site information such as language, email, phone, and title.
 * 5. Initializes and updates the header, navigation, footer, and important dates on the page.
 *
 * @async
 * @function fetchAppDocket
 * @throws {Error} Throws an error if the API response status is greater than 299 or if there is an error during the fetch operation.
 */

async function fetchAppDocket() {
    try {
        // Resolve the CMS docket from the URL path (root -> bare appDocket;
        // a configured first segment -> "<appDocket>/<segment>"). The base
        // before "?" is unchanged so the shared appDocket fetch guard still
        // inspects the payload.
        const appDocketURL = resolveDocketAPI(
            `${appConfigProd}/website`,
            appDocket,
            window.location.pathname,
            pathDockets
        );

        // Fetch data from the app docket API
        const response = await fetch(appDocketURL);

        // Check the response status and handle errors
        if (response.status > 299) {
            // This error will init the catch block
            throw new Error("App Docket API Error", { cause: response });
        }

        // Parse the response JSON data
        const data = await response.json();

        // Update site information
        setSiteInfoLang(() => data.findResult.lang[userLanguage]);
        setSiteInfo(() => data.findResult);
        setEmailInform(() => data.findResult.email_info);
        setPhoneAdmin(() => data.findResult.phone);
        setEmailConfirm(() => data.findResult.email_conf);

        // Update the document title
        document.title = siteInfoLang.SiteTitle;

        // Page slug (shifted past the docket segment inside a subsite) and pages
        const domainSlug = resolvePageSlug(
            window.location.pathname,
            pathDockets
        );
        // Hide the submit-claim nav link / "Submit Claim" button while the
        // claim form is suppressed, then append the "Other Settlements" link
        // back to the root hub on the settlement subsites.
        const basePages = CLAIM_FORM_SUPPRESSED
            ? withoutSubmitClaim(siteInfoLang.Pages)
            : siteInfoLang.Pages;
        const sitePages = withOtherSettlementsLink(
            basePages,
            pathDockets,
            window.location.pathname
        );

        // Initialize the header, footer, and important dates
        const header = new Header(
            siteInfoLang.SiteName,
            siteInfoLang.CaseNumber,
            siteInfoLang.CourtName
        );
        const importantDates = new ImportantDates(data);
        const footer = new Footer();

        // Header
        const headerElem = document.querySelector("header");
        headerElem.classList.add("py-3");
        headerElem.appendChild(header.createHeader());

        // Routing is driven by this site's docket-aware router instead of the
        // shared Navigation.pathChange (which would read the docket segment as a
        // page slug).
        initPathRouting({
            sitePages,
            importantDates,
            getPage,
            pathDockets,
            i18n,
        });

        // Navigation bar renders for the settlement subsites only. The root hub
        // is a thin router (links + info card) with no page navigation.
        if (currentDocketSlug(window.location.pathname, pathDockets)) {
            const navigation = new Navigation(sitePages, domainSlug, getPage);
            document
                .querySelector("header")
                .insertAdjacentHTML("afterend", navigation.element.outerHTML);
        }

        document.querySelector("footer").appendChild(footer.element);

        // Get the page content
        await getPage(sitePages, domainSlug, importantDates);
    } catch (error) {
        // Log and handle errors
        errorHandler.log_exception(
            new Error("Error fetching app docket", { cause: error })
        );
        errorHandler.display(error);

        // Hide the loading gif
        loadingGif(loading, "hide", 1000);
    }
}

// Fetch the application docket when the DOM content is loaded
document.addEventListener("DOMContentLoaded", fetchAppDocket);
